/**
 * DatabaseApp browser commands.
 *
 * These thin wrappers forward every call to the tool-runtime handler via
 * `callToolRuntime`. Keeping them here means the LLM schema (parameters) is
 * defined once per tool and both the AI and any manual command-palette caller
 * go through the same code path.
 */

import type { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import type { StandardToolResult } from '../../../types/standardToolResults';
import { callToolRuntime } from '../../../utils/callToolRuntime';
import { schemaToParameters } from '../../../utils/schemaToParameters';
import { createSuccessResult, createErrorResult } from '../../../types/standardToolResults';

function wrapTool(
  id: string,
  description: string,
  inputSchema: Record<string, any>,
): SelfContainedCommand {
  return {
    id,
    description,
    autonomyLevel: 'auto',
    visualTreatment: 'silent',
    schema: { parameters: schemaToParameters(inputSchema) },
    examples: { good: [], bad: [] },
    handler: async (
      params: any,
      context: CommandExecutionContext,
    ): Promise<StandardToolResult> => {
      console.log(`[DATABASE_CMD] ${id}`, params);
      // `context.spaceId` is only set when the command is invoked from AI chat.
      // When invoked via commandRegistryStore (sidebar, command palette), the
      // third context argument is absent — spaceId lives in `params.spaceId`.
      const enrichedContext = {
        ...context,
        spaceId: context?.spaceId || params?.spaceId || '',
      };
      return callToolRuntime(id, params, enrichedContext);
    },
  };
}

// Keep schemas aligned with tool-runtime/src/tools/table_*.ts MANIFESTs.

const COLUMN_TYPE_ENUM = [
  'text',
  'number',
  'select',
  'multiselect',
  'date',
  'checkbox',
  'url',
  'email',
  'entity_ref',
];

// table_create: sidebar-friendly creation command.
//
// When invoked from the sidebar "+" menu (handleCreateEntityViaCommand),
// context.spaceId is absent and name/title are undefined. We create the
// Table entity directly via entityStore.actions.createEntity (same pattern
// as notes_create.ts) so the sidebar's inline-rename flow kicks in without
// needing a round-trip through the tool-runtime.
//
// When invoked from AI chat, name will be provided as a param; the tool-runtime
// handler (table_create in tool-runtime/src/tools/table_create.ts) is the
// authoritative path for that case.
export const table_create: SelfContainedCommand = {
  id: 'table_create',
  description: 'Create a Notion-style Table entity with columns, rows, and optional query-mode population.',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        name: { type: 'string' },
        description: { type: 'string' },
        columns: { type: 'array', items: { type: 'object' } },
        rowMode: { type: 'string', enum: ['manual', 'query'], default: 'manual' },
        rowEntityType: { type: 'string' },
        query: { type: 'object' },
        folderId: { type: 'string' },
      },
      required: [],
    }),
  },
  examples: { good: [], bad: [] },
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    console.log('[DATABASE_CMD] table_create', params);

    // Resolve spaceId — sidebar passes it in params; AI chat has it in context.
    const spaceId: string = context?.spaceId || params?.spaceId || '';
    const folderId: string | undefined = params?.folderId;
    const name: string = (params?.name || params?.title || '').trim() || 'New Table';

    // When called from AI with a name, delegate to tool-runtime for the full
    // Y.Doc seeding + broadcast path.
    if (params?.name?.trim()) {
      const enrichedContext = { ...context, spaceId };
      return callToolRuntime('table_create', params, enrichedContext);
    }

    // Sidebar path: create entity directly so the inline-rename flow works.
    try {
      const { default: useEntityStore } = await import('../../../stores/entityStore');
      const { defaultTableSkeleton } = await import('../services/tableYjsSchema');
      const store = useEntityStore.getState();
      const skeleton = defaultTableSkeleton();

      if (folderId) (skeleton as any).groupIds = [folderId];

      const entity = await store.actions.createEntity({
        entityType: 'Table',
        name,
        spaceId,
        skeleton: {
          ...skeleton,
          '@type': 'Table',
          updatedAt: new Date().toISOString(),
          ...(folderId ? { groupIds: [folderId] } : {}),
        },
      } as any);

      const entityId = entity._id || (entity as any).id;
      console.log('[DATABASE_CMD] table_create ok (direct)', { entityId, name: entity.name });

      return createSuccessResult(`Created table "${entity.name}"`, {
        entityId,
        tableId: entityId,
        llmId: (entity as any).llmId,
        name: entity.name,
      });
    } catch (err: any) {
      console.error('[DATABASE_CMD] table_create failed', err);
      return createErrorResult('Failed to create table', err.message || 'Unknown error', 'execution');
    }
  },
};

export const table_get = wrapTool(
  'table_get',
  'Read a Table schema (columns, views, rowMode) and its current rows.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      viewId: { type: 'string' },
      limit: { type: 'number', default: 200 },
    },
    required: ['tableId'],
  },
);

export const table_update = wrapTool(
  'table_update',
  'Update Table metadata: name, description, rowMode, query spec.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      patch: { type: 'object' },
    },
    required: ['tableId', 'patch'],
  },
);

export const table_add_column = wrapTool(
  'table_add_column',
  'Add a new column to a Table.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      column: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          type: { type: 'string', enum: COLUMN_TYPE_ENUM },
          options: { type: 'array', items: { type: 'object' } },
          refEntityType: { type: 'string' },
          description: { type: 'string' },
        },
        required: ['name', 'type'],
      },
    },
    required: ['tableId', 'column'],
  },
);

export const table_remove_column = wrapTool(
  'table_remove_column',
  'Remove a column from a Table.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      columnId: { type: 'string' },
    },
    required: ['tableId', 'columnId'],
  },
);

export const table_add_row = wrapTool(
  'table_add_row',
  'Add a new TableRow to a Table with cell values.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      cells: { type: 'object' },
      afterRowId: { type: 'string' },
    },
    required: ['tableId'],
  },
);

export const table_update_cell = wrapTool(
  'table_update_cell',
  'Set a cell value on a row (TableRow or linked entity).',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      rowId: { type: 'string' },
      columnId: { type: 'string' },
      value: {},
    },
    required: ['tableId', 'rowId', 'columnId'],
  },
);

export const table_delete_row = wrapTool(
  'table_delete_row',
  'Delete a row from a Table.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      rowId: { type: 'string' },
    },
    required: ['tableId', 'rowId'],
  },
);

export const table_link_entity = wrapTool(
  'table_link_entity',
  'Attach an existing entity as a row in a Table via an in_table edge.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      entityId: { type: 'string' },
      cellOverrides: { type: 'object' },
    },
    required: ['tableId', 'entityId'],
  },
);

export const table_unlink_entity = wrapTool(
  'table_unlink_entity',
  'Remove an in_table edge so the entity no longer appears in the Table.',
  {
    type: 'object',
    properties: {
      tableId: { type: 'string' },
      entityId: { type: 'string' },
    },
    required: ['tableId', 'entityId'],
  },
);

export const table_query = wrapTool(
  'table_query',
  'Run an ad-hoc entity query and optionally materialize results as a new query-mode Table.',
  {
    type: 'object',
    properties: {
      entityType: { type: 'string' },
      filters: { type: 'object' },
      searchQuery: { type: 'string' },
      parentFolderId: { type: 'string' },
      limit: { type: 'number', default: 100 },
      materializeAsTable: { type: 'boolean', default: false },
      tableName: { type: 'string' },
      columns: { type: 'array', items: { type: 'object' } },
    },
    required: [],
  },
);

export const DATABASE_COMMANDS = [
  table_create,
  table_get,
  table_update,
  table_add_column,
  table_remove_column,
  table_add_row,
  table_update_cell,
  table_delete_row,
  table_link_entity,
  table_unlink_entity,
  table_query,
];
